import {IHierarchyNodeProvider, INode, INodeLogicalKey} from "../PA.MD.Core/PA.MD.IF.Hierarchy";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Entity, esWB_MasterData_Set, esWB_Period, esWB_Period_Relationship
} from "../PA.Core/PAdbSchema";
import * as _ from "lodash"
import * as AsyncLock from 'async-lock';
import {ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {WB_PeriodRow, WB_EntityRow, WB_MasterDataSetRow} from "../PA.Core/PAdbSchemaRows";
import {HierarchyUtils} from "../PA.Core/Basic/HierarchyUtils";
import {PeriodNode} from "./PeriodNode";


const knownMappers = {
    [esWB_Period]: esWB_Period
}

export class PeriodNodeProvider
    implements IHierarchyNodeProvider {
    private lkNodeLock:AsyncLock

    private dbContext: PAdbContext
    private wbMDSetId: number
    private masterDataSetRow: WB_MasterDataSetRow
    private dctByWbId: { [mapperId: string]: { [wbId: number]: INode } }
    private dctByKeySetId: { [mapperId: string]: { [keySetId: string]: { [Id: string]: INode } } }
    private dctByLKHash: { [hashKey: string]: INode }

    private loaded: boolean
    private arrEntityTypes: WB_EntityRow[]

    constructor(dbContext: PAdbContext, wbMDSetId: number) {
        this.dbContext = dbContext
        this.wbMDSetId = wbMDSetId
        this.dctByWbId = {[esWB_Period]: {}}
        this.dctByKeySetId = {[esWB_Period]: {}}
        this.dctByLKHash = {}
        this.masterDataSetRow = undefined
        this.loaded = false
        this.lkNodeLock = new AsyncLock({})
        this.arrEntityTypes = []
    }

    get bLoaded(): boolean { return this.loaded }

    async OnLoadComplete(): Promise<void> {
        await this.checkLoaded()
    }

    private async checkLoaded(){
        if(this.loaded)
            return

        this.masterDataSetRow = <WB_MasterDataSetRow> await this.dbContext.find(esWB_MasterData_Set, this.wbMDSetId)

        this.arrEntityTypes = <WB_EntityRow[]> await this.dbContext.findAll(esWB_Entity, {
            where: {
                WB_MDSET_ID: this.wbMDSetId,
                ENTITY_DIMENSION: "Period",
                DELETED_IND: {neq: '1'}
            }
        })

        this.loaded = true

    }

    // Async, read from Source as needed, loads into cache
    //-- Find one
    async findNodeByWbId(mapperName: string, wbId: number): Promise<INode> {
        await this.checkLoaded()

        let arr = await this.findNodesByWbIds(mapperName, [wbId])
        if (arr.length)
            return arr[0]

        return undefined
    }


    async findNodeByKeySetId(mapperName: string, sKeySetId: string, sId: string): Promise<INode> {
        await this.checkLoaded()

        let arr = await this.findNodesByKeySetIds(mapperName, sKeySetId, [sId])
        if (arr.length)
            return arr[0]

        return undefined
    }

    //-- Find multiple
    async findNodesByWbIds(mapperName: string, wbIds: number[]): Promise<INode[]> {
        await this.checkLoaded()

        if (!(mapperName in knownMappers))
            return []

        let searchIds: number[] = []
        let rv: INode[] = []
        _.forEach(wbIds, (wbId) => {
            let nd = this.findNodeByWbIdSync(mapperName, wbId)
            if (_.isEmpty(nd))
                searchIds.push(wbId)
            else
                rv.push(nd)
        })

        if (_.isEmpty(searchIds))
            return rv

        let rr = this.getRecordResolver()
        let rows: WB_PeriodRow[] = <WB_PeriodRow[]>await rr.findAllWithRelationships(mapperName, searchIds)
        for (let aRow of rows) {
            let cn = new PeriodNode(aRow, this)
            this.addNodeToCache(cn)
            rv.push(cn)
        }

        return rv
    }

    async findNodesByKeySetIds(mapperName: string, sKeySetId: string, arrIds: string[]): Promise<INode[]> {
        await this.checkLoaded()

        if (!(mapperName in knownMappers))
            return []

        let searchIds: string[] = []
        let rv: INode[] = []
        _.forEach(arrIds, (sId) => {
            let nd = this.findNodeByKeySetIdSync(mapperName, sKeySetId, sId)
            if (_.isEmpty(nd))
                searchIds.push(sId)
            else
                rv.push(nd)
        })

        if (_.isEmpty(searchIds))
            return rv

        let rr = this.getRecordResolver()
        let queryCnd = {
            WB_MDSET_ID: this.wbMDSetId,
            PERIOD_SET_ID: sKeySetId,
            PERIOD_ID: {inq: searchIds},
            DELETED_IND: {neq: '1'}
        }
        let rows: WB_PeriodRow[] = <WB_PeriodRow[]>await rr.queryAllWithRelationships(mapperName, {
            where: queryCnd
        })
        for (let aRow of rows) {
            let cn = new PeriodNode(aRow, this)
            this.addNodeToCache(cn)
            rv.push(cn)
        }

        return rv

    }

    // Sync, pull from cache only
    findNodeByWbIdSync(mapperName: string, wbId: number): INode {
        if (!(mapperName in knownMappers))
            return undefined

        let dct = this.dctByWbId[mapperName] || {}
        if (wbId in dct)
            return dct[wbId]

        return undefined
    }

    findNodeByKeySetIdSync(mapperName: string, keySetId: string, id: string): INode {
        if (!(mapperName in knownMappers))
            return undefined

        let dctL1 = this.dctByKeySetId[mapperName] || {}
        if (!(keySetId in dctL1))
            return undefined

        let dctL2 = dctL1[keySetId]
        if (!(id in dctL2))
            return undefined

        return dctL2[id]
    }

    findNodeByLogicalKeySync(logicalKey: INodeLogicalKey): INode {

        let sLKHash = HierarchyUtils.getLogicKeyHash(logicalKey)

        if(_.isEmpty(sLKHash))
            return undefined
        if(sLKHash in this.dctByLKHash)
            return this.dctByLKHash[sLKHash]

        return undefined
    }

    async findNodeByLogicalKey(logicalKey: INodeLogicalKey): Promise<INode> {
        return undefined
    }

    // async for MD Generated Hierarchy
    // A. Check in Cache
    // B. Check in DB
    // C. Create if not found
    async findOrCreateLogicalKeyNode(logicalKey: INodeLogicalKey, options?: {sCreateId?: string, sCreateName?: string}): Promise<INode> {
        await this.checkLoaded()
        // Not implemented for Period Hierarchy
        return undefined
    }

    get entityTypes(): WB_EntityRow[] {
        return this.arrEntityTypes
    }

    // Private Functions
    private addNodeToCache(aNode: INode): void {
        if (_.isEmpty(aNode))
            return

        let mapperId = aNode.mapperName
        let wbId = aNode.wbId
        let keySetId = aNode.key_set
        let id_ui = aNode.id_ui

        // Always add to wbId dictionary
        if (!(mapperId in this.dctByWbId))
            this.dctByWbId[mapperId] = {}
        if (!(wbId in this.dctByWbId[mapperId]))
            this.dctByWbId[mapperId][wbId] = aNode

        if (aNode.bUseLogicalKey) {
            let sLKHash = HierarchyUtils.getLogicKeyHash(aNode.logicalKey)
            this.dctByLKHash[sLKHash] = aNode
        }

        if (!(mapperId in this.dctByKeySetId))
            this.dctByKeySetId[mapperId] = {}
        if (!(keySetId in this.dctByKeySetId[mapperId]))
            this.dctByKeySetId[mapperId][keySetId] = {}
        if (!(id_ui in this.dctByKeySetId[mapperId][keySetId]))
            this.dctByKeySetId[mapperId][keySetId][id_ui] = aNode

    }





    private getRecordResolver(): ESRecordResolver {
        let rr = new ESRecordResolver(this.dbContext, {
            standardConditions: {
                [esWB_Period]: {"DELETED_IND": {"notIn": ['1']}}
            },
            dctExcludeHasManyNamedRelations: {
                // We only want: header record
                [esWB_Period]: [
                    esWB_Period_Relationship
                ]
            },
            dctIncludeBelongsToNamedRelations: {}
        })

        return rr
    }


}